"use client"

import React, { useRef, useEffect, useState, useCallback } from 'react'
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd'
import { Tab } from './tab'
import { Package, MessageSquare, MessageSquarePlus, History, Terminal } from 'lucide-react'
import { ScrollArea, ScrollBar } from "@sourcepilot/ui/framework/ui/atoms/scroll-area"
import { cn } from '@sourcepilot/ui/framework/data/utils'
import { iconMap } from '@sourcepilot/ui/framework/ui/atoms/icons'
import { Button } from '@sourcepilot/ui/framework/ui/atoms/button'
import { useRouter, useSearchParams, usePathname } from 'next/navigation'

import { useAtom, useAtomValue } from 'jotai'
import { NewTabData, useTabData, patchStateAtom } from '@sourcepilot/ui/framework/state/session-data';
import { useAppHost } from '@sourcepilot/ui/framework/state/AppHostContext';
import { useStartProjectChat } from '@sourcepilot/ui/framework/data/hooks/use-start-project-chat';

interface TabAreaProps {
}

export function TabArea({ }: TabAreaProps) {
  const { tabs, activeTab, removeTab, reorderTabs, navigateToTab } = useTabData()
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const chatParam = searchParams.get('chat');

  // Hide chat button on standalone pages (root, chat pages)
  const isStandalonePage = pathname === '/' || pathname.includes('/chat/');

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const tabAreaRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
  const [isMouseOverTabs, setIsMouseOverTabs] = useState(false);
  const { currentEntityAtom, currentEntityTaskAtom, createNewChatTaskForCurrentEntity } = useAppHost();
  const [ patchState, ] = useAtom(patchStateAtom);

  // Current navigation entity + task attached to it — both derived by the
  // host from its own domain (PDM Product + its current_tasks relationship,
  // a Document and its conversation, …). Ui only reads id/name.
  const currentEntity = useAtomValue(currentEntityAtom);
  const entityTask = useAtomValue(currentEntityTaskAtom);
  // on a project page: a chat of the project's own (the entity is a product's)
  const projectChat = useStartProjectChat();

  // Fix hydration error: only enable drag-and-drop after client mount
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const onDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) {
      return
    }
    reorderTabs(result.source.index, result.destination.index)
  }, [reorderTabs]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (isMouseOverTabs && scrollContainerRef.current) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        scrollContainerRef.current.scrollBy({ left: -100, behavior: 'smooth' })
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        scrollContainerRef.current.scrollBy({ left: 100, behavior: 'smooth' })
      }
    }
  }, [isMouseOverTabs])

  const scrollTabIntoView = useCallback((activeTab: string) => {
    const tabElement = tabRefs.current[activeTab]
    if (tabElement && scrollContainerRef.current) {
      tabElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      })
    }
  }, []);

  useEffect(() => {
    if (activeTab) {
      const activeTabElement = tabRefs.current[activeTab.pathname];
      if (activeTabElement && scrollContainerRef.current) {
        requestAnimationFrame(() => {
          scrollTabIntoView(activeTab.pathname);
        });
      }
    }
  }, [activeTab]);

  useEffect(() => {
    const tabArea = tabAreaRef.current

    if (tabArea) {
      const handleMouseEnter = () => {
        setIsMouseOverTabs(true)
      }
      const handleMouseLeave = () => setIsMouseOverTabs(false)

      tabArea.addEventListener('mouseenter', handleMouseEnter)
      tabArea.addEventListener('mouseleave', handleMouseLeave)
      document.addEventListener('keydown', handleKeyDown)

      return () => {
        tabArea.removeEventListener('mouseenter', handleMouseEnter)
        tabArea.removeEventListener('mouseleave', handleMouseLeave)
        document.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [handleKeyDown])

  // Ensure activeTab is set when tabs exist
  // IMPORTANT: This should ONLY run when we're truly at root with no active tab
  // NOT during tab removal when we're already navigating
  useEffect(() => {
    // Only auto-select a tab if:
    // 1. There are tabs available
    // 2. No active tab is selected
    // 3. We're actually at the root path (not in the middle of navigation)
    if (!activeTab && tabs.length > 0 && pathname === '/') {
      navigateToTab(tabs[0]);
    }
  }, [activeTab, tabs, navigateToTab, pathname]);

  const threadMenuWidth = 95;

  return (
    <div className={`w-full flex`}>
      <div className="flex-1 overflow-hidden">
        {/* tabs */}
        <div className="w-full tab-scroll-container relative overflow-hidden flex flex-col bg-gray-100 border-r border-gray-200" ref={tabAreaRef}>
          <ScrollArea className="w-full" ref={scrollContainerRef} orientation="horizontal">
            {isMounted && (
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="tabs" direction="horizontal">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="flex h-full min-w-0"
                    >
                      {tabs.map((tab, index) => {
                        const isActive = activeTab?.pathname === tab.pathname;
                        return (
                        <Draggable key={`${tab.id}_${tab.contentType}`} draggableId={tab.id || `tab-${index}`} index={index} >
                          {(provided, snapshot) => (
                            <div
                              ref={(el) => {
                                tabRefs.current[tab.pathname] = el
                                provided.innerRef(el)
                              }}
                              data-tab-id={tab.id}
                              className={cn(
                                "relative flex-shrink-0",
                                isActive && "z-10"
                              )}
                            >
                              <Tab
                                id={`${tab.id}_${tab.contentType}`}
                                title={tab.title}
                                isActive={isActive}
                                onClick={() => navigateToTab(tab)}
                                onClose={(e) => {
                                  e.stopPropagation()
                                  removeTab(tab)
                                }}
                                provided={provided}
                                snapshot={snapshot}
                                icon={(iconMap[tab.contentType] || Package)}
                              />
                            </div>
                          )}
                        </Draggable>
                      )})}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
            <ScrollBar orientation="horizontal" className="z-20">
              <div className="relative flex-1 rounded-full transition-all" />
            </ScrollBar>
          </ScrollArea>
        </div>
      </div>

      {/* right menu buttons - hidden on standalone pages */}
      {!isStandalonePage && (
        <div className={`w-[${threadMenuWidth}px] flex justify-end h-full`}>
          <div className="flex items-center mr-[5px] ">
            {/* Chat open button (only when no chat is shown — an open chat is
                collapsed/expanded from the chat panel's own top bar, not here):
                - If existing task: open it (MessageSquare icon)
                - If no task but has entity: create new attached chat (MessageSquarePlus icon)
            */}
            {!chatParam && (
              <Button variant="ghost" size="sm" data-testid="tab-chat-button" disabled={projectChat?.busy}
                title={projectChat?.failed ? 'The chat could not be started. Try again.' : undefined}
                onClick={async () => {
                  if (projectChat) {
                    await projectChat.start();
                  } else if (entityTask?.id) {
                    // Open existing chat
                    const newParams = new URLSearchParams(searchParams);
                    newParams.set('chat', entityTask.id);
                    router.push(`${pathname}?${newParams.toString()}`);
                  } else if (currentEntity?.id) {
                    // No existing task — host creates a new ChatTask attached
                    // to the current entity (all the Task.save() /
                    // currently_attached_to.set() domain logic lives in the
                    // host's createNewChatTaskForCurrentEntity).
                    const newTask = await createNewChatTaskForCurrentEntity();
                    if (newTask?.id) {
                      const newParams = new URLSearchParams(searchParams);
                      newParams.set('chat', newTask.id);
                      router.push(`${pathname}?${newParams.toString()}`);
                    }
                  }
                }
                }>
                {entityTask?.id
                  ? <MessageSquare className="w-5 h-5" />
                  : <MessageSquarePlus className="w-5 h-5" />}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>    
  )
}

